import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Grid3x3, Phone, PhoneMissed } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useMissedCalls } from '../../hooks/useMissedCalls';
import { DialPad } from './DialPad';
import { MissedCallsList } from './MissedCallsList';

type Tab = 'missed' | 'pad';

/**
 * Header entry of the telephony: a phone icon badged with the callers still to
 * call back, opening a panel with the missed calls and a dial pad. Rendered only
 * for users holding a telephony seat (gated by the caller).
 */
export function DialerMenu({
  buttonClassName,
  openClassName,
  align = 'right',
}: {
  /** The button's look where the default (the header's) does not fit — Encre's sidebar. */
  buttonClassName?: string;
  /** Its look while the panel is open. */
  openClassName?: string;
  /** Which edge of the button the panel aligns to: the sidebar sits on the left of the screen. */
  align?: 'left' | 'right';
} = {}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('missed');
  const { calls, loading, error, markHandled } = useMissedCalls(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const count = calls.length;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = () => {
    // Open on what needs attention: callers to call back first, the pad otherwise.
    if (!open) setTab(count > 0 ? 'missed' : 'pad');
    setOpen((o) => !o);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={count > 0 ? `Téléphone — ${count} appel${count > 1 ? 's' : ''} à rappeler` : 'Téléphone'}
        className={
          buttonClassName
            ? twMerge(buttonClassName, open && openClassName)
            : twMerge(
                'relative flex items-center rounded-lg p-2 transition-colors',
                open ? 'bg-desk-subtle text-desk-ink' : 'text-desk-muted hover:bg-desk-subtle hover:text-desk-ink',
              )
        }
      >
        <Phone className="h-4 w-4" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-red-500 px-1 text-center text-xs font-semibold leading-4 text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Téléphone"
          className={twMerge(
            'fixed inset-x-2 top-14 z-50 overflow-hidden rounded-xl border border-desk-line bg-desk-card shadow-pop sm:absolute sm:inset-x-auto sm:top-full sm:mt-1 sm:w-80',
            align === 'left' ? 'sm:left-0' : 'sm:right-0',
          )}
        >
          <div className="flex border-b border-desk-line" role="tablist">
            <TabButton active={tab === 'missed'} onClick={() => setTab('missed')}>
              <PhoneMissed className="h-3.5 w-3.5" />
              À rappeler{count > 0 ? ` (${count})` : ''}
            </TabButton>
            <TabButton active={tab === 'pad'} onClick={() => setTab('pad')}>
              <Grid3x3 className="h-3.5 w-3.5" />
              Clavier
            </TabButton>
          </div>
          {tab === 'missed' ? (
            <MissedCallsList
              calls={calls}
              loading={loading}
              error={error}
              onHandled={(n) => void markHandled(n)}
              onOpenPad={() => setTab('pad')}
            />
          ) : (
            <DialPad onCallStarted={() => setOpen(false)} />
          )}
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={twMerge(
        'flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2.5 text-xs font-medium transition-colors',
        active
          ? 'border-desk-ring text-desk-ink'
          : 'border-transparent text-desk-muted hover:text-desk-ink',
      )}
    >
      {children}
    </button>
  );
}
